import React, { useState, useMemo } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  ArrowLeft, Github, Zap, ChevronDown, ChevronRight, Database, Sparkles,
  Calculator, GitBranch, Workflow, Network, Mic, Building2, Ban, Gauge,
  Search, AlertTriangle, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  BRAIN_MODULES, BRAIN_CONNECTIONS,
  CALCULATION_DETAILS, FUNCTION_CALL_GRAPH, DATA_FLOW_STAGES, VARIABLE_DEPENDENCIES,
  TONE_DEFINITIONS, INDUSTRY_CONTEXTS, AI_SLOP_BLACKLIST, TOKEN_LIMITS
} from '../../data/brainModules';
import LayerView from './LayerView';

// Styles
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#030712',
    color: 'white',
  },
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 40,
    backgroundColor: 'rgba(17, 24, 39, 0.8)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid #1f2937',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#9ca3af',
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
  divider: {
    height: '24px',
    width: '1px',
    backgroundColor: '#374151',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  githubButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    backgroundColor: '#1f2937',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  hero: {
    paddingTop: '80px',
    paddingBottom: '32px',
    paddingLeft: '16px',
    paddingRight: '16px',
    textAlign: 'center',
    background: 'linear-gradient(to bottom, #111827, #030712)',
  },
  title: {
    fontSize: 'clamp(24px, 5vw, 48px)',
    fontWeight: 700,
    marginBottom: '16px',
  },
  gradientText: {
    background: 'linear-gradient(to right, #a78bfa, #ec4899, #fb923c)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    color: '#9ca3af',
    maxWidth: '640px',
    margin: '0 auto',
    fontSize: 'clamp(14px, 2vw, 16px)',
    lineHeight: 1.6,
  },
  hints: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '24px',
  },
  hint: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#6b7280',
  },
  hintDot: (color) => ({
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: color,
  }),
  canvas: {
    height: 'calc(100vh - 280px)',
  },
  badge: {
    position: 'fixed',
    bottom: '16px',
    left: '16px',
    zIndex: 30,
    backgroundColor: 'rgba(31, 41, 55, 0.9)',
    backdropFilter: 'blur(4px)',
    border: '1px solid #374151',
    borderRadius: '8px',
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  mobileList: {
    padding: '16px',
  },
  mobileCard: (color) => ({
    width: '100%',
    backgroundColor: 'rgba(31, 41, 55, 0.5)',
    border: `1px solid ${color}40`,
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  mobileCardIcon: (color) => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    backgroundColor: `${color}30`,
    flexShrink: 0,
  }),
  mobileCardText: {
    textAlign: 'left',
    flex: 1,
  },
  mobileCardTitle: {
    color: 'white',
    fontWeight: 500,
    marginBottom: '4px',
  },
  mobileCardSubtitle: {
    color: '#6b7280',
    fontSize: '14px',
  },
  // Deep Dive Section Styles
  deepDiveSection: {
    padding: '40px 16px',
    backgroundColor: '#0a0f1a',
    borderTop: '1px solid #1f2937',
  },
  deepDiveHeader: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  deepDiveTitle: {
    fontSize: '28px',
    fontWeight: 700,
    marginBottom: '8px',
  },
  deepDiveSubtitle: {
    color: '#9ca3af',
    fontSize: '14px',
  },
  tabsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '24px',
    maxWidth: '900px',
    margin: '0 auto 24px auto',
  },
  tab: (isActive, color) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '8px',
    border: isActive ? `2px solid ${color}` : '2px solid #374151',
    backgroundColor: isActive ? `${color}20` : 'transparent',
    color: isActive ? color : '#9ca3af',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '13px',
    fontWeight: 500,
  }),
  tabContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: 'rgba(31, 41, 55, 0.5)',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #374151',
  },
  // Calculation styles
  calcGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '16px',
  },
  calcCard: (color) => ({
    backgroundColor: 'rgba(17, 24, 39, 0.8)',
    border: `1px solid ${color}40`,
    borderRadius: '12px',
    padding: '16px',
  }),
  calcHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  calcIcon: (color) => ({
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: `${color}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  }),
  calcTitle: {
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
  },
  calcFormula: {
    backgroundColor: '#1a1a2e',
    padding: '12px',
    borderRadius: '8px',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: '#a78bfa',
    marginBottom: '12px',
    overflowX: 'auto',
  },
  calcDetails: {
    fontSize: '12px',
    color: '#9ca3af',
    lineHeight: 1.6,
  },
  // Function graph styles
  funcList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  funcCard: (color) => ({
    backgroundColor: 'rgba(17, 24, 39, 0.8)',
    border: `1px solid ${color}40`,
    borderRadius: '12px',
    padding: '16px',
  }),
  funcHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  funcName: {
    fontFamily: 'monospace',
    color: '#22d3ee',
    fontSize: '14px',
    fontWeight: 600,
  },
  funcIO: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  funcInputs: {
    backgroundColor: '#1a2332',
    padding: '12px',
    borderRadius: '8px',
  },
  funcOutputs: {
    backgroundColor: '#1a2332',
    padding: '12px',
    borderRadius: '8px',
  },
  funcLabel: {
    color: '#6b7280',
    fontSize: '10px',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  funcItem: {
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#9ca3af',
    padding: '2px 0',
  },
  // Tone styles
  toneGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  },
  toneCard: (color, isExpanded) => ({
    backgroundColor: 'rgba(17, 24, 39, 0.8)',
    border: `2px solid ${isExpanded ? color : color + '40'}`,
    borderRadius: '12px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  toneHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  toneIcon: (color) => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: `${color}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  }),
  toneName: {
    color: 'white',
    fontWeight: 600,
    fontSize: '16px',
  },
  toneDesc: {
    color: '#9ca3af',
    fontSize: '12px',
  },
  toneBestFor: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '12px',
  },
  toneTag: (color) => ({
    backgroundColor: `${color}20`,
    color: color,
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '10px',
  }),
  // Industry styles
  industrySearch: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    backgroundColor: '#1f2937',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #374151',
  },
  industryInput: {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
  },
  industryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '12px',
  },
  industryCard: (color) => ({
    backgroundColor: 'rgba(17, 24, 39, 0.8)',
    border: `1px solid ${color}40`,
    borderRadius: '12px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  industryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  industryIcon: (color) => ({
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: `${color}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  }),
  industryName: {
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
  },
  industryContext: {
    color: '#9ca3af',
    fontSize: '11px',
    lineHeight: 1.5,
  },
  // Slop styles
  slopFilters: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  slopFilter: (isActive, color) => ({
    padding: '6px 12px',
    borderRadius: '6px',
    backgroundColor: isActive ? `${color}20` : 'transparent',
    border: `1px solid ${isActive ? color : '#374151'}`,
    color: isActive ? color : '#9ca3af',
    cursor: 'pointer',
    fontSize: '12px',
  }),
  slopList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  slopItem: (severity) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: severity === 'high' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(251, 191, 36, 0.1)',
    border: `1px solid ${severity === 'high' ? '#ef444440' : '#fbbf2440'}`,
    borderRadius: '8px',
  }),
  slopText: {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: 'white',
  },
  slopMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  slopCategory: (color) => ({
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: `${color}20`,
    color: color,
    fontSize: '10px',
  }),
  // Token styles
  tokenGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  },
  tokenCard: (color) => ({
    backgroundColor: 'rgba(17, 24, 39, 0.8)',
    border: `1px solid ${color}40`,
    borderRadius: '12px',
    padding: '20px',
  }),
  tokenHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  tokenIcon: (color) => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: `${color}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  }),
  tokenName: {
    color: 'white',
    fontWeight: 600,
    fontSize: '18px',
  },
  tokenRange: {
    color: '#9ca3af',
    fontSize: '12px',
  },
  tokenBar: {
    height: '24px',
    backgroundColor: '#1f2937',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '12px',
    position: 'relative',
  },
  tokenFill: (color, width) => ({
    height: '100%',
    width: `${width}%`,
    backgroundColor: color,
    borderRadius: '12px',
    transition: 'width 0.5s ease',
  }),
  tokenTarget: (left) => ({
    position: 'absolute',
    left: `${left}%`,
    top: 0,
    bottom: 0,
    width: '3px',
    backgroundColor: 'white',
    borderRadius: '2px',
  }),
  tokenDesc: {
    color: '#9ca3af',
    fontSize: '12px',
    lineHeight: 1.5,
  },
};

// Handle styles for edge connections
const handleStyles = {
  source: {
    background: '#6b7280',
    width: '8px',
    height: '8px',
    border: 'none',
  },
  target: {
    background: '#6b7280',
    width: '8px',
    height: '8px',
    border: 'none',
  },
};

// Custom Node Component for Brain Neurons
const NeuronNode = ({ data, selected }) => {
  const nodeStyle = {
    position: 'relative',
    cursor: 'pointer',
    transition: 'transform 0.3s',
    transform: selected ? 'scale(1.1)' : 'scale(1)',
  };

  const glowStyle = {
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    filter: 'blur(20px)',
    opacity: 0.5,
    backgroundColor: data.color,
  };

  const mainNodeStyle = {
    position: 'relative',
    backgroundColor: '#111827',
    borderRadius: '16px',
    padding: '16px',
    border: selected ? '2px solid white' : '2px solid #374151',
    transition: 'all 0.3s',
    boxShadow: selected ? `0 0 30px ${data.color}60` : `0 0 20px ${data.color}30`,
    minWidth: '160px',
  };

  const iconContainerStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    marginBottom: '12px',
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: `${data.color}30`,
  };

  const pulseStyle = {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: data.color,
    animation: 'pulse 2s infinite',
  };

  return (
    <div style={nodeStyle} onClick={data.onClick}>
      {/* Handles for edge connections */}
      <Handle type="target" position={Position.Top} style={handleStyles.target} />
      <Handle type="source" position={Position.Bottom} style={handleStyles.source} />
      <Handle type="target" position={Position.Left} id="left" style={handleStyles.target} />
      <Handle type="source" position={Position.Right} id="right" style={handleStyles.source} />

      <div style={glowStyle} />
      <div style={mainNodeStyle}>
        <div style={iconContainerStyle}>
          {data.icon}
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'white', fontWeight: 500, fontSize: '14px', lineHeight: 1.3 }}>
            {data.friendlyTitle}
          </p>
          <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
            {data.title}
          </p>
        </div>
        <div style={pulseStyle} />
      </div>
    </div>
  );
};

// Node types registration
const nodeTypes = {
  neuron: NeuronNode,
};

const BrainExplorer = () => {
  const [selectedModule, setSelectedModule] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Deep Dive state
  const [deepDiveOpen, setDeepDiveOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('calculations');
  const [expandedTone, setExpandedTone] = useState(null);
  const [industrySearch, setIndustrySearch] = useState('');
  const [slopFilter, setSlopFilter] = useState('all');

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Convert modules to React Flow nodes
  const initialNodes = useMemo(() =>
    BRAIN_MODULES.map((module) => ({
      id: module.id,
      type: 'neuron',
      position: module.position,
      data: {
        ...module,
        onClick: () => setSelectedModule(module),
      },
    })),
    []
  );

  // Convert connections to React Flow edges
  const initialEdges = useMemo(() =>
    BRAIN_CONNECTIONS.map((conn) => ({
      ...conn,
      type: 'smoothstep',
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: conn.style.stroke,
      },
    })),
    []
  );

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  // Mobile list view
  const MobileListView = () => (
    <div style={styles.mobileList}>
      {BRAIN_MODULES.map((module) => (
        <div
          key={module.id}
          onClick={() => setSelectedModule(module)}
          style={styles.mobileCard(module.color)}
        >
          <div style={styles.mobileCardIcon(module.color)}>
            {module.icon}
          </div>
          <div style={styles.mobileCardText}>
            <p style={styles.mobileCardTitle}>{module.friendlyTitle}</p>
            <p style={styles.mobileCardSubtitle}>{module.title}</p>
          </div>
          <ChevronRight color="#6b7280" size={20} />
        </div>
      ))}
    </div>
  );

  return (
    <div style={styles.page}>
      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.1); }
        }
        .react-flow__controls {
          background: #1f2937 !important;
          border: 1px solid #374151 !important;
          border-radius: 8px !important;
        }
        .react-flow__controls-button {
          background: #1f2937 !important;
          border-bottom: 1px solid #374151 !important;
          fill: #9ca3af !important;
        }
        .react-flow__controls-button:hover {
          background: #374151 !important;
        }
      `}</style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Link to="/" style={styles.backLink}>
              <ArrowLeft size={20} />
              <span style={{ display: isMobile ? 'none' : 'inline' }}>Back</span>
            </Link>
            <div style={styles.divider} />
            <div style={styles.logoSection}>
              <Zap color="#facc15" size={20} />
              <span style={{ fontWeight: 600 }}>ReviewResponder Brain</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => document.getElementById('deep-dive-section')?.scrollIntoView({ behavior: 'smooth' })}
              style={{...styles.githubButton, background: 'linear-gradient(135deg, #8B5CF6, #EC4899)', border: 'none'}}
            >
              <Sparkles size={16} />
              <span style={{ display: isMobile ? 'none' : 'inline' }}>Deep Dive ↓</span>
            </button>
            <a
              href="https://github.com/berschlend/review-responder"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.githubButton}
            >
              <Github size={16} />
              <span style={{ display: isMobile ? 'none' : 'inline' }}>Source</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div style={styles.hero}>
        <h1 style={styles.title}>
          You want to look inside my head? 🤨
        </h1>
        <p style={styles.subtitle}>
          Okay, that's actually a pretty big invasion of my privacy... but for you, I'll make an exception.
        </p>
      </div>

      {/* React Flow Canvas - works on all devices */}
      <div style={styles.canvas}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.3}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: isMobile ? 0.5 : 0.8 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#374151" gap={20} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      {/* Deep Dive Section - Collapsible */}
      <div id="deep-dive-section" style={styles.deepDiveSection}>
        <div
          style={{...styles.deepDiveHeader, cursor: 'pointer'}}
          onClick={() => setDeepDiveOpen(!deepDiveOpen)}
        >
          <h2 style={styles.deepDiveTitle}>
            {deepDiveOpen ? '🫣' : '🔒'} Okay, that's enough now...
            <span style={{ fontSize: '16px', marginLeft: '12px', color: '#6b7280' }}>
              {deepDiveOpen ? '▲ collapse' : '▼ expand'}
            </span>
          </h2>
          <p style={styles.deepDiveSubtitle}>
            {deepDiveOpen
              ? 'Fine, look at everything then. But don\'t say I didn\'t warn you.'
              : 'Do you REALLY want to go deeper into my head? This is getting creepy...'}
          </p>
        </div>

        {/* Tabs - only show when open */}
        {deepDiveOpen && (
          <>
            <div style={styles.tabsContainer}>
          {[
            { id: 'calculations', label: 'Calculations', icon: Calculator, color: '#10B981' },
            { id: 'functions', label: 'Functions', icon: GitBranch, color: '#8B5CF6' },
            { id: 'dataflow', label: 'Data Flow', icon: Workflow, color: '#F59E0B' },
            { id: 'dependencies', label: 'Dependencies', icon: Network, color: '#EC4899' },
            { id: 'tones', label: 'Tones', icon: Mic, color: '#14B8A6' },
            { id: 'industries', label: 'Industries', icon: Building2, color: '#3B82F6' },
            { id: 'slop', label: 'Slop Blacklist', icon: Ban, color: '#EF4444' },
            { id: 'tokens', label: 'Tokens', icon: Gauge, color: '#6366F1' },
          ].map(tab => (
            <div
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={styles.tab(activeTab === tab.id, tab.color)}
            >
              <tab.icon size={14} />
              <span>{tab.label}</span>
            </div>
          ))}
        </div>

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {/* Calculations Tab */}
          {activeTab === 'calculations' && (
            <div style={styles.calcGrid}>
              {Object.entries(CALCULATION_DETAILS).map(([key, calc]) => (
                <div key={key} style={styles.calcCard('#10B981')}>
                  <div style={styles.calcHeader}>
                    <div style={styles.calcIcon('#10B981')}>📊</div>
                    <div>
                      <div style={styles.calcTitle}>{key}</div>
                    </div>
                  </div>
                  <div style={styles.calcFormula}>{calc.formula || calc.function}</div>
                  <div style={styles.calcDetails}>
                    {calc.inputs && typeof calc.inputs === 'object' && (
                      <>
                        <strong style={{ color: '#fff' }}>Inputs:</strong>{' '}
                        {Object.keys(calc.inputs).join(', ')}
                        <br />
                      </>
                    )}
                    {calc.thresholds && (
                      <>
                        <strong style={{ color: '#fff' }}>Thresholds:</strong>{' '}
                        {Object.entries(calc.thresholds).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ')}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Functions Tab */}
          {activeTab === 'functions' && (
            <div style={styles.funcList}>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
                <div style={{ color: '#22d3ee', fontFamily: 'monospace', marginBottom: '8px' }}>
                  Entry Point: {FUNCTION_CALL_GRAPH.entryPoint}
                </div>
              </div>
              {(FUNCTION_CALL_GRAPH.callSequence || []).map((step, idx) => (
                <div key={idx} style={styles.funcCard('#8B5CF6')}>
                  <div style={styles.funcHeader}>
                    <span style={styles.funcName}>Step {step.step}: {step.function}()</span>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>{step.purpose}</span>
                  </div>
                  <div style={styles.funcInputs}>
                    <div style={styles.funcLabel}>Calls</div>
                    {(step.calls || []).map((call, i) => (
                      <div key={i} style={styles.funcItem}>{call}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Data Flow Tab */}
          {activeTab === 'dataflow' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {DATA_FLOW_STAGES.map((stage, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  backgroundColor: 'rgba(17, 24, 39, 0.8)',
                  borderRadius: '12px',
                  border: `1px solid ${stage.color}40`,
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: `${stage.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                  }}>
                    {stage.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'white', fontWeight: 600, marginBottom: '4px' }}>{stage.name}</div>
                    <div style={{ color: '#9ca3af', fontSize: '12px' }}>{stage.description}</div>
                  </div>
                  <div style={{ color: stage.color, fontSize: '24px' }}>→</div>
                </div>
              ))}
            </div>
          )}

          {/* Dependencies Tab */}
          {activeTab === 'dependencies' && (
            <div>
              {/* Primary Inputs */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#EC4899', marginBottom: '12px', fontSize: '16px' }}>Primary Inputs</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {(VARIABLE_DEPENDENCIES.primaryInputs || []).map((input, i) => (
                    <span key={i} style={{
                      padding: '8px 12px',
                      backgroundColor: 'rgba(236, 72, 153, 0.2)',
                      border: '1px solid #EC489940',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      color: '#EC4899',
                    }}>
                      {input}
                    </span>
                  ))}
                </div>
              </div>
              {/* Intermediates */}
              <h3 style={{ color: '#8B5CF6', marginBottom: '12px', fontSize: '16px' }}>Intermediate Variables</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                {Object.entries(VARIABLE_DEPENDENCIES.intermediates || {}).map(([key, dep]) => (
                  <div key={key} style={{
                    padding: '16px',
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    borderRadius: '12px',
                    border: '1px solid #8B5CF640',
                  }}>
                    <div style={{
                      fontFamily: 'monospace',
                      color: '#8B5CF6',
                      fontWeight: 600,
                      marginBottom: '12px',
                      fontSize: '14px',
                    }}>
                      {key}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                      Depends on:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                      {(dep.dependsOn || []).map((d, i) => (
                        <span key={i} style={{
                          padding: '4px 8px',
                          backgroundColor: '#1f2937',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          color: '#9ca3af',
                        }}>
                          {d}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                      Feeds into:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {(dep.feedsInto || []).map((f, i) => (
                        <span key={i} style={{
                          padding: '4px 8px',
                          backgroundColor: '#10B98120',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          color: '#10B981',
                        }}>
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tones Tab */}
          {activeTab === 'tones' && (
            <div style={styles.toneGrid}>
              {Object.entries(TONE_DEFINITIONS).map(([key, tone]) => (
                <div
                  key={key}
                  style={styles.toneCard(tone.color, expandedTone === key)}
                  onClick={() => setExpandedTone(expandedTone === key ? null : key)}
                >
                  <div style={styles.toneHeader}>
                    <div style={styles.toneIcon(tone.color)}>{tone.icon}</div>
                    <div>
                      <div style={styles.toneName}>{tone.name}</div>
                      <div style={styles.toneDesc}>{tone.description}</div>
                    </div>
                  </div>
                  <div style={styles.toneBestFor}>
                    {tone.bestFor.map((item, i) => (
                      <span key={i} style={styles.toneTag(tone.color)}>{item}</span>
                    ))}
                  </div>
                  {expandedTone === key && tone.example && (
                    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>EXAMPLE</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                        <strong style={{ color: '#fff' }}>Review:</strong> {tone.example.review}
                      </div>
                      <div style={{ fontSize: '12px', color: tone.color }}>
                        <strong style={{ color: '#fff' }}>Response:</strong> {tone.example.response}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Industries Tab */}
          {activeTab === 'industries' && (
            <>
              <div style={styles.industrySearch}>
                <Search size={16} color="#6b7280" />
                <input
                  type="text"
                  placeholder="Search industries..."
                  value={industrySearch}
                  onChange={(e) => setIndustrySearch(e.target.value)}
                  style={styles.industryInput}
                />
                {industrySearch && (
                  <X size={16} color="#6b7280" style={{ cursor: 'pointer' }} onClick={() => setIndustrySearch('')} />
                )}
              </div>
              <div style={styles.industryGrid}>
                {Object.entries(INDUSTRY_CONTEXTS)
                  .filter(([key, ind]) =>
                    ind.name.toLowerCase().includes(industrySearch.toLowerCase()) ||
                    ind.keywords?.some(k => k.toLowerCase().includes(industrySearch.toLowerCase()))
                  )
                  .map(([key, industry]) => (
                    <div key={key} style={styles.industryCard(industry.color)}>
                      <div style={styles.industryHeader}>
                        <div style={styles.industryIcon(industry.color)}>{industry.icon}</div>
                        <div style={styles.industryName}>{industry.name}</div>
                      </div>
                      <div style={styles.industryContext}>{industry.context}</div>
                      {industry.keywords && (
                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {industry.keywords.slice(0, 4).map((kw, i) => (
                            <span key={i} style={{
                              padding: '2px 6px',
                              backgroundColor: `${industry.color}20`,
                              color: industry.color,
                              borderRadius: '4px',
                              fontSize: '10px',
                            }}>
                              {kw}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}

          {/* Slop Blacklist Tab */}
          {activeTab === 'slop' && (
            <>
              <div style={styles.slopFilters}>
                {['all', 'corporate', 'hyperbolic', 'robotic-empathy', 'filler'].map(filter => (
                  <div
                    key={filter}
                    onClick={() => setSlopFilter(filter)}
                    style={styles.slopFilter(slopFilter === filter, '#EF4444')}
                  >
                    {filter === 'all' ? 'All' : filter}
                  </div>
                ))}
              </div>
              <div style={styles.slopList}>
                {[
                  ...(AI_SLOP_BLACKLIST.phrases?.items || []),
                  ...(AI_SLOP_BLACKLIST.words?.items || [])
                ]
                  .filter(item => slopFilter === 'all' || item.category === slopFilter)
                  .map((item, idx) => (
                    <div key={idx} style={styles.slopItem(item.severity)}>
                      <span style={styles.slopText}>"{item.text}"</span>
                      <div style={styles.slopMeta}>
                        <span style={styles.slopCategory(item.severity === 'high' ? '#EF4444' : '#FBBF24')}>
                          {item.category}
                        </span>
                        {item.severity === 'high' && <AlertTriangle size={14} color="#EF4444" />}
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}

          {/* Token Limits Tab */}
          {activeTab === 'tokens' && (
            <div style={styles.tokenGrid}>
              {Object.entries(TOKEN_LIMITS)
                .filter(([key]) => key !== 'bracketMultiplier')
                .map(([key, limit]) => (
                  <div key={key} style={styles.tokenCard(limit.color)}>
                    <div style={styles.tokenHeader}>
                      <div style={styles.tokenIcon(limit.color)}>{limit.icon}</div>
                      <div>
                        <div style={styles.tokenName}>{limit.name}</div>
                        <div style={styles.tokenRange}>{limit.min} - {limit.max} tokens</div>
                      </div>
                    </div>
                    <div style={styles.tokenBar}>
                      <div style={styles.tokenFill(limit.color, (limit.max / 500) * 100)} />
                      <div style={styles.tokenTarget((limit.target / 500) * 100)} />
                    </div>
                    <div style={styles.tokenDesc}>
                      <strong style={{ color: '#fff' }}>Target:</strong> {limit.target} tokens
                      <br />
                      <strong style={{ color: '#fff' }}>Use for:</strong> {limit.sentences}
                      <br />
                      {limit.useCase}
                    </div>
                  </div>
                ))}
              {TOKEN_LIMITS.bracketMultiplier && (
                <div style={{
                  ...styles.tokenCard('#A78BFA'),
                  gridColumn: 'span 2',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={styles.tokenIcon('#A78BFA')}>×</div>
                    <div>
                      <div style={styles.tokenName}>Bracket Multiplier</div>
                      <div style={styles.tokenRange}>{TOKEN_LIMITS.bracketMultiplier.value}x overhead</div>
                    </div>
                  </div>
                  <div style={styles.tokenDesc}>
                    {TOKEN_LIMITS.bracketMultiplier.description}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
          </>
        )}
      </div>

      {/* Layer Detail Modal */}
      {selectedModule && (
        <LayerView
          module={selectedModule}
          onClose={() => setSelectedModule(null)}
          onNavigateToModule={(newModule) => {
            // Navigate to a related module (sideways navigation)
            setSelectedModule(newModule);
          }}
        />
      )}
    </div>
  );
};

export default BrainExplorer;
